import { FC, useState } from 'react';
import {
  Button,
  useGetMany,
  useListContext,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
  useUnselectAll,
} from 'react-admin';
import { AssignmentInd as AssignIcon } from '@mui/icons-material';
import { BatchAssignUnit } from 'services/api/useBatchAssignUnits';
// BatchAssignModal is created in TVR-497-4
import BatchAssignModal from './BatchAssignModal';

const BatchAssignBulkAction: FC = () => {
  const { selectedIds } = useListContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll('units');
  const { permissions } = usePermissions();
  const translate = useTranslate();

  const hasPermission = permissions?.includes('manage_units');

  // Fetch ALL selected units by ID — useListContext().data only contains the
  // current page, so cross-page selections would be silently dropped otherwise.
  const { data: selectedUnitsData = [] } = useGetMany<BatchAssignUnit>(
    'units',
    { ids: selectedIds },
    { enabled: selectedIds.length > 0 },
  );

  const selectedUnits: BatchAssignUnit[] = selectedUnitsData.map((unit) => ({
    id: unit.id,
    serial: unit.serial ?? null,
    hostname: unit.hostname ?? null,
    hardware_revision_id: unit.hardware_revision_id,
    update_channel_id: unit.update_channel_id ?? null,
  }));

  const handleSuccess = (successCount: number, failureCount: number) => {
    if (failureCount === 0) {
      notify('resources.units.bulk_assign.success_all', {
        type: 'success',
        messageArgs: { count: successCount },
      });
    } else {
      notify('resources.units.bulk_assign.success_partial', {
        type: 'warning',
        messageArgs: { successCount, failureCount },
      });
    }
    unselectAll();
    refresh();
    setIsModalOpen(false);
  };

  if (!hasPermission || selectedIds.length === 0) return null;

  return (
    <>
      <Button
        label={translate('resources.units.bulk_assign.button')}
        onClick={() => setIsModalOpen(true)}
        startIcon={<AssignIcon />}
        variant="text"
      />
      <BatchAssignModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        units={selectedUnits}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default BatchAssignBulkAction;
